import {roundToTwoDecimals} from '@Utils/math.util';
import {formatNumberInUSD} from '@Utils/number.util';
import type {TicketingConfig, TicketingFeeParams} from '@Components/event-wizard/event-wizard-ticketing/event-wizard-ticketing.types';
import {TaxMode} from '@Components/event-wizard/event-wizard-ticketing/event-wizard-ticketing.types';
import {TicketingPlanLimitState} from '@Components/event-ticketing/event-ticketing.types';

export interface TicketPriceBreakdown {
  /** The platform rate this breakdown priced at — the overage rate once the organiser is past their allowance. */
  serviceFeeRatePercent: number;
  facePrice: number; // pre-tax amount the organiser keeps (before fees)
  taxAmount: number; // 0 when there is no tax
  serviceFee: number; // PMW platform fee (P)
  processingFee: number; // payment-processing recovery (C)
  attendeePays: number; // buyer total
  payout: number; // organiser gross receipt = attendeePays − P − C
}

const PERCENT_DIVISOR = 100;
const MS_PER_DAY = 86_400_000;

/**
 * An event qualifies for payout this many days after it ends. Mirrors backend
 * `EventTicketingPayoutFacade::PAYOUT_DELAY_DAYS`.
 *
 * Exported because the breakdown cards state this number to the organiser ("Released at least N days after your event
 * ends"). Interpolating it keeps that promise tied to the code that keeps it — a literal in the copy would silently go
 * stale the day this changes.
 */
export const PAYOUT_QUALIFY_DELAY_DAYS = 7;

/**
 * Days of the month the payout cron runs (00:00 UTC). Must stay in sync with
 * `EventTicketingPayoutFacade::PAYOUT_RUN_DAYS_OF_MONTH` and the `0 0 1,15 * *` crontab entry.
 */
const PAYOUT_RUN_DAYS_OF_MONTH = [1, 15] as const;

export const fetchTicketingFeeParams = async (): Promise<TicketingFeeParams> => {
  return (await window.PMW.writeLocal('eventticketing/getTicketingFeeParams')) as TicketingFeeParams;
};

/**
 * Mirrors the real charge model (post Managed-Risk) so the preview matches checkout. The two fees ride on
 * DIFFERENT bases: the platform (P) fee on the pre-tax face (not levied on tax — legal requirement), and the
 * processing (C) fee on the tax-inclusive face + tax (Stripe bills its processing cost on the full charged
 * amount, so we recover it on the same base). No gross-up. The application fee equals P + C, so the
 * organiser's gross receipt is always `attendeePays − P − C`: exactly face + tax in pass mode, and
 * face + tax − fees in absorb mode. Tax flows to the organiser (they remit it). INCLUSIVE tax backs the
 * face out of the typed price by subtraction so the buyer pays exactly the typed amount; EXCLUSIVE adds tax
 * on top. The platform fee is clamped to the server-resolved per-ticket cap (null = uncapped); the rate and
 * cap themselves are resolved server-side — the client only applies the min, and swaps in the overage rate
 * once the organiser has passed their plan's allowance (see getEffectivePlatformFeeRate).
 */
export const computeTicketPriceBreakdown = (config: Pick<TicketingConfig, 'price' | 'passFeesToBuyer' | 'taxRatePercent' | 'taxMode'>, fees: TicketingFeeParams): TicketPriceBreakdown => {
  const {price: ticketPrice, passFeesToBuyer, taxRatePercent, taxMode} = config;
  const taxRate = taxRatePercent / PERCENT_DIVISOR;
  const hasTax = taxRate > 0 && ticketPrice > 0;

  let facePrice = ticketPrice;
  let taxAmount = 0;
  if (hasTax && taxMode === TaxMode.INCLUSIVE) {
    facePrice = roundToTwoDecimals(ticketPrice / (1 + taxRate));
    taxAmount = roundToTwoDecimals(ticketPrice - facePrice); // by subtraction → buyer pays exactly the typed price
  } else if (hasTax) {
    taxAmount = roundToTwoDecimals(facePrice * taxRate);
  }

  const taxInclusive = roundToTwoDecimals(facePrice + taxAmount); // what the buyer pays for the ticket (tax included)
  // Platform fee on the pre-tax face only; processing fee on the tax-inclusive amount (matches the charge).
  const {percent: platformPercent, cap: platformCap} = getEffectivePlatformFeeRate(fees);
  const uncapped = facePrice * (platformPercent / PERCENT_DIVISOR) + fees.serviceFeeFixed;
  const serviceFee = roundToTwoDecimals(platformCap === null ? uncapped : Math.min(uncapped, platformCap));
  const processingFee = roundToTwoDecimals(taxInclusive * (fees.processingFeePercent / PERCENT_DIVISOR) + fees.processingFeeFixed);

  const attendeePays = passFeesToBuyer ? roundToTwoDecimals(taxInclusive + serviceFee + processingFee) : taxInclusive;
  const payout = roundToTwoDecimals(attendeePays - serviceFee - processingFee);

  return {serviceFeeRatePercent: platformPercent, facePrice, taxAmount, serviceFee, processingFee, attendeePays, payout};
};

/**
 * The platform rate and per-ticket cap the organiser's next order will actually be charged at.
 *
 * Below their plan's allowance that is the plan's own rate and cap. Once they have passed it, the fee flips
 * to the overage rate and the cap comes off entirely — mirroring the fork's `resolvePlatformRateAndCap`, so
 * the preview stops understating the fee for an organiser who is already over.
 *
 * A plan whose allowance carries no overage rate (PAYG) never flips: passing it costs nothing.
 */
export const getEffectivePlatformFeeRate = (fees: TicketingFeeParams): {percent: number; cap: number | null} => {
  const {state, overagePercent} = fees.planLimit;
  if (state !== TicketingPlanLimitState.CROSSED || overagePercent === null) {
    return {percent: fees.serviceFeePercent, cap: fees.serviceFeeCap};
  }
  return {percent: overagePercent, cap: null};
};

export const formatFeePercent = (percent: number): string => {
  return `${roundToTwoDecimals(percent)}%`;
};

/**
 * A fee's rate as shown to organisers: "3.5%" on its own, or "2.9% + $0.30" when the fee also has a fixed part.
 * Shared by the wizard price preview and the dashboard earnings breakdown so both read the rate the same way.
 */
export const formatTicketFeeRate = (percent: number, fixedAmount: number): string => {
  if (fixedAmount > 0) {
    return `${formatFeePercent(percent)} + ${formatNumberInUSD(fixedAmount)}`;
  }
  return formatFeePercent(percent);
};

/**
 * The organiser's actual payout date: the next scheduled twice-monthly run (a PAYOUT_RUN_DAYS_OF_MONTH
 * day at 00:00 UTC) on or after the event qualifies (event end + PAYOUT_QUALIFY_DELAY_DAYS). Mirrors
 * `EventTicketingPayoutFacade::nextScheduledPayoutDateUnix` on the backend.
 */
export const getTicketingPayoutDate = (eventEndDateTime: Date): Date => {
  return getFirstPayoutRunOnOrAfter(eventEndDateTime.getTime() + PAYOUT_QUALIFY_DELAY_DAYS * MS_PER_DAY);
};

/**
 * The next payout run that can still pay this event: the first run on or after it qualifies, or — once that run has
 * passed (the event qualified long ago and money is still owed, e.g. a sale or a released hold since) — the next run
 * from now. The dashboard's "Estimated next payout" reads this so it never quotes a date in the past.
 */
export const getTicketingNextPayoutDate = (eventEndDateTime: Date, now: Date): Date => {
  const qualifyMs = eventEndDateTime.getTime() + PAYOUT_QUALIFY_DELAY_DAYS * MS_PER_DAY;
  return getFirstPayoutRunOnOrAfter(Math.max(qualifyMs, now.getTime()));
};

/** The first run day (PAYOUT_RUN_DAYS_OF_MONTH, 00:00 UTC) at or after the given instant. */
const getFirstPayoutRunOnOrAfter = (instantMs: number): Date => {
  const instant = new Date(instantMs);
  const year = instant.getUTCFullYear();
  const month = instant.getUTCMonth();
  const runDates: number[] = [];

  for (let monthOffset = 0; monthOffset <= 1; monthOffset++) {
    for (const dayOfMonth of PAYOUT_RUN_DAYS_OF_MONTH) {
      runDates.push(Date.UTC(year, month + monthOffset, dayOfMonth));
    }
  }
  runDates.sort((a, b) => a - b);

  return new Date(runDates.find((runMs) => runMs >= instantMs) ?? instantMs);
};

export const openTicketingPricingPage = (): void => {
  window.open(window.PMW.util.site_url('premium'), '_blank');
};
